import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Skeleton } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RUN_ENTRY_STATUS_CHIP, type RunListRow } from '../../salariesMeta';

type PayrollRunListProps = {
    rows: RunListRow[];
    /** The runs feed is still in flight — no row count is known yet. */
    isLoading: boolean;
    onOpenRun: (runId: string) => void;
};

/**
 * Committed payroll runs, newest first.
 *
 * A HeroUI Pro `DataGrid`, like every register on `/accounting`. It renders a
 * real `<table>` underneath, with the period as the row header; its accessible
 * name is `aria-label` (DataGrid has no `<caption>` slot).
 *
 * A preview never appears here — a run exists only once it has been committed.
 * But committed is **not** posted: a run is created as a DRAFT entry and has
 * charged nothing until an accountant posts it, which is why every row carries
 * its entry's status. Without that column a run that has moved no money would
 * read exactly like one that has. The empty state says a fresh register
 * genuinely has no runs, rather than implying the feature is broken.
 *
 * It is shown **only once the feed has resolved**. The register and the runs load
 * independently, so an empty `rows` while the query is in flight means "not known
 * yet", not "none" — and announcing "No payroll runs yet" over the user's own
 * posted entries is exactly the kind of confident falsehood this screen is built
 * to avoid.
 *
 * The due date column carries the statutory filing and payment deadline (the 15th
 * of the following month) as **information**. Nothing on this screen files
 * anything.
 */
export const PayrollRunList: React.FC<PayrollRunListProps> = ({
    rows,
    isLoading,
    onOpenRun,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<RunListRow>[]>(
        () => [
            {
                id: 'period',
                header: t('salaries.runs.columns.period'),
                isRowHeader: true,
                minWidth: 160,
                cell: (row) => (
                    <button
                        className="cursor-[var(--cursor-interactive)] text-left font-medium text-foreground underline-offset-2 hover:underline"
                        onClick={() => onOpenRun(row.id)}
                        type="button"
                        {...getNodeTestIdProp(
                            `salaries-run-list-open-${row.id}`,
                        )}
                    >
                        {row.periodText}
                    </button>
                ),
            },
            {
                id: 'entryStatus',
                header: t('salaries.runs.columns.entryStatus'),
                minWidth: 150,
                cell: (row) => (
                    <span className="flex flex-wrap items-center gap-2">
                        <span
                            className={cn(
                                'rounded-lg px-2 py-0.5 text-xs font-medium',
                                RUN_ENTRY_STATUS_CHIP[row.entryStatus]
                                    .className,
                            )}
                            {...getNodeTestIdProp(
                                `salaries-run-list-status-${row.id}`,
                            )}
                        >
                            {t(RUN_ENTRY_STATUS_CHIP[row.entryStatus].labelKey)}
                        </span>
                        {row.entryNumberText ? (
                            <span className="whitespace-nowrap text-xs tabular-nums text-muted">
                                {row.entryNumberText}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'employees',
                header: t('salaries.runs.columns.employees'),
                align: 'end',
                minWidth: 120,
                cell: (row) => (
                    <span className="tabular-nums">
                        {row.paidEmployeeCount}
                    </span>
                ),
            },
            {
                id: 'gross',
                header: t('salaries.runs.columns.gross'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums">
                        {row.grossText}
                    </span>
                ),
            },
            {
                id: 'net',
                header: t('salaries.runs.columns.net'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums">
                        {row.netText}
                    </span>
                ),
            },
            {
                id: 'employerCost',
                header: t('salaries.runs.columns.employerCost'),
                align: 'end',
                minWidth: 150,
                cell: (row) => (
                    <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                        {row.employerCostText}
                    </span>
                ),
            },
            {
                id: 'dueDate',
                header: t('salaries.runs.columns.dueDate'),
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums text-muted">
                        {row.dueDateText}
                    </span>
                ),
            },
        ],
        [onOpenRun, t],
    );

    if (isLoading) {
        return (
            <div
                className="flex flex-col gap-2 rounded-2xl border border-separator bg-surface p-4"
                {...getNodeTestIdProp('salaries-runs-loading')}
            >
                {[0, 1].map((row) => (
                    <Skeleton className="h-9 w-full" key={row} />
                ))}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-10 text-center"
                {...getNodeTestIdProp('salaries-runs-empty')}
            >
                <Icon
                    aria-hidden
                    className="size-7 text-muted"
                    icon="gravity-ui:calendar"
                />
                <span className="text-sm font-medium text-foreground">
                    {t('salaries.runs.empty.title')}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t('salaries.runs.empty.body')}
                </span>
            </div>
        );
    }

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the list's
    // testid has nowhere to land on the grid itself.
    return (
        <div className="contents" {...getNodeTestIdProp('salaries-runs-table')}>
            <DataGrid<RunListRow>
                aria-label={t('salaries.runs.tableAria')}
                className="ledger-data-grid"
                columns={columns}
                contentClassName="min-w-[720px]"
                data={rows}
                getRowId={(row) => row.id}
            />
        </div>
    );
};
